import { muteGainAt, type Clip } from "@hudhud/core";
import { FPS } from "../types.js";
import { clampMap } from "./interpolate.js";

const dbToGain = (db: number) => Math.pow(10, db / 20);

/** Where the speech starts and ends on the clip timeline (first to last caption word). */
export function speechSpan(clip: Pick<Clip, "captions">): { startMs: number; endMs: number } | null {
  const words = clip.captions.flatMap((c) => c.words);
  if (!words.length) return null;
  return { startMs: Math.min(...words.map((w) => w.startMs)), endMs: Math.max(...words.map((w) => w.endMs)) };
}

export const DUCK_IN_MS = 350;
export const DUCK_OUT_MS = 900;

/** Music gain at an absolute reel time; `offsetMs` is where the speech starts (after a full-screen opener), so ducking lines up with the words.
 *  "steady" holds one lowered level from the first word to the last (no swell in pauses); "words" follows each run of words. */
export function musicGainAt(clip: Pick<Clip, "captions" | "music">, env: { ms: number; db: number }[], ms: number, totalMs: number, offsetMs = 0, mutes: { inMs: number; outMs: number }[] = []): number {
  const base = dbToGain(clip.music.volumeDb) * muteGainAt(mutes, ms - offsetMs);
  let duckDb = 0;
  const speechMs = ms - offsetMs;
  if (clip.music.duckMode === "steady") {
    const span = speechSpan(clip);
    if (span) {
      const down = clampMap(speechMs, [span.startMs - DUCK_IN_MS, span.startMs], [0, 1]);
      const up = clampMap(speechMs, [span.endMs, span.endMs + DUCK_OUT_MS], [1, 0]);
      duckDb = clip.music.duckDb * Math.min(down, up);
    }
  } else {
    for (const p of env) { if (p.ms <= speechMs) duckDb = p.db; else break; }
  }
  const fade = clip.music.fadeMs;
  const fadeIn = clampMap(ms, [0, fade], [0, 1]);
  const fadeOut = clampMap(ms, [totalMs - fade, totalMs], [1, 0]);
  return base * dbToGain(duckDb) * fadeIn * fadeOut;
}

/** Short fade at the edges of each loop pass so the join never clicks. */
export const EDGE_FRAMES = Math.round(0.25 * FPS);
export function edgeFade(localFrame: number, sectionFrames: number): number {
  const a = clampMap(localFrame, [0, EDGE_FRAMES], [0, 1]);
  const b = clampMap(localFrame, [sectionFrames - EDGE_FRAMES, sectionFrames], [1, 0]);
  return Math.min(a, b);
}
